import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  CaseReducer,
} from '@reduxjs/toolkit';

import authService from '@app/services/auth';

import { Token } from '@app/models/token';
import { UserInfo } from '@app/models/user-info';
import { Permission } from '@app/models/permission';
import userService from '@app/services/user';
import { RequestLoading } from '@app/utils/constants';
import { handleErrorResponse } from '@app/utils/helpers';
import { ChangePasswordArgs } from '@app/pages/forgot-password';

const requests = [
  'login',
  'loginWithCookie',
  'getUserInfo',
  'forgetPassword',
  'confirmChangePassword',
] as const;

type AuthRequest = RequestLoading<(typeof requests)[number]>;

interface State {
  token: Token | null;
  tokenExpiredTime: Date | null;
  loginLoading: boolean;
  userInfo: UserInfo | null;
  getUserInfoLoading: boolean;
  getSSOLoading: boolean;
  permissionList: Permission[];
  authRequest: AuthRequest;
}

const initialState: State = {
  token: null,
  tokenExpiredTime: null,
  loginLoading: false,
  userInfo: null,
  getUserInfoLoading: false,
  getSSOLoading: false,
  permissionList: [
    // { code: 'ADMIN' },
    // { code: 'ADMIN_MODULE' },
    // { code: 'ADMIN_ACCOUNT' },
    // { code: 'ADMIN_ROLE' },
    // { code: 'CSYT_VACCINATION' },
    // { code: 'CSYT_TELEMEDICINE' },
  ],
  authRequest: {
    loading: false,
  },
};

type CR<T> = CaseReducer<State, PayloadAction<T>>;

const TYPE_PREFIX = 'auth/';

const login = createAsyncThunk<Token, { username: string; password: string }>(
  `${TYPE_PREFIX}login`,
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const result = await authService.login(username, password);
      return result;
    } catch (error) {
      return rejectWithValue(handleErrorResponse(error));
    }
  },
);

const loginWithCookie = createAsyncThunk(
  `${TYPE_PREFIX}loginWithCookie`,
  async () => {
    const result = await userService.getUserInfoWithToken();
    return result;
  },
);

const forgetPassword = createAsyncThunk<void, string>(
  `${TYPE_PREFIX}forgetPassword`,
  async (username, { rejectWithValue }) => {
    try {
      return await authService.forgetPassword(username);
    } catch (error) {
      rejectWithValue(handleErrorResponse(error));
    }
  },
);

const changePassword = createAsyncThunk<unknown, ChangePasswordArgs>(
  `${TYPE_PREFIX}changePassword`,
  async (data, { rejectWithValue }) => {
    try {
      const result = await authService.changePassword(data);
      return result;
    } catch (error) {
      return rejectWithValue(handleErrorResponse(error));
    }
  },
);

const setTokenCR: CR<{
  token: Token;
}> = (state, action) => ({
  ...state,
  token: action.payload.token,
});

const getUserInfo = createAsyncThunk<Token, void>(
  'auth/getUserInfo',
  async (_, { rejectWithValue }) => {
    try {
      const result = await authService.getUserInfo();
      // window.document.title = result.name || result.username;
      return result;
    } catch (error) {
      return rejectWithValue(handleErrorResponse(error));
    }
  },
);

const logoutCR: CR<void> = () => ({
  ...initialState,
});

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setToken: setTokenCR,
    logout: logoutCR,
    setPermisstionList: (state, action) => {
      // state.permissionList = action.payload;
    },
  },
  extraReducers: (builder) => {
    // login
    builder.addCase(login.pending, (state, action) => ({
      ...state,
      authRequest: {
        loading: true,
        name: 'login',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(login.fulfilled, (state, action) => {
      // const { username } = payload;
      return {
        ...state,
        // loginLoading: false,
        authRequest: {
          loading: false,
          name: 'login',
          status: action.meta.requestStatus,
        },
        token: action.payload,
        // tokenExpiredTime: new Date(
        //   new Date().getTime() + payload.expires_in * 1000,
        // ),
        // permissionList:
        //   username === '1'
        //     ? [{ code: 'ADMIN' }, { code: 'CSYT_CATALOG' }]
        //     : initialState.permissionList,
      };
    });
    builder.addCase(login.rejected, (state, action) => ({
      ...state,
      // loginLoading: false,
      authRequest: {
        loading: false,
        name: 'login',
        status: action.meta.requestStatus,
        error: action.payload ?? action.error,
      },
    }));

    builder.addCase(loginWithCookie.pending, (state) => ({
      ...state,
      // validCookie: true,
      getSSOLoading: true,
    }));
    builder.addCase(loginWithCookie.fulfilled, (state, { payload }) => {
      const { username } = payload;
      return {
        ...state,
        // validCookie: true,
        getSSOLoading: false,
        token: payload,
        tokenExpiredTime: new Date(
          new Date().getTime() + payload.expires_in * 1000,
        ),
        permissionList:
          username === '1'
            ? [{ code: 'ADMIN' }, { code: 'CSYT_CATALOG' }]
            : initialState.permissionList,
      };
    });
    builder.addCase(loginWithCookie.rejected, (state) => ({
      ...state,
      // validCookie: false,
      getSSOLoading: false,
    }));

    builder.addCase(forgetPassword.pending, (state, action) => ({
      ...state,
      authRequest: {
        name: 'forgetPassword',
        loading: true,
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(forgetPassword.fulfilled, (state, action) => ({
      ...state,
      authRequest: {
        name: 'forgetPassword',
        loading: false,
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(forgetPassword.rejected, (state, action) => ({
      ...state,
      authRequest: {
        name: 'forgetPassword',
        loading: false,
        status: action.meta.requestStatus,
        error: action.payload ?? action.error,
      },
    }));

    builder.addCase(changePassword.pending, (state, action) => ({
      ...state,
      authRequest: {
        name: 'confirmChangePassword',
        loading: true,
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(changePassword.fulfilled, (state, action) => ({
      ...state,
      authRequest: {
        name: 'confirmChangePassword',
        loading: false,
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(changePassword.rejected, (state, action) => ({
      ...state,
      authRequest: {
        name: 'confirmChangePassword',
        loading: false,
        status: action.meta.requestStatus,
        error: action.payload ?? action.error,
      },
    }));
  },
});

export { login, getUserInfo, loginWithCookie, changePassword, forgetPassword };
export const { setToken, logout, setPermisstionList } = slice.actions;

export default slice.reducer;
